import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

interface SpinnerOverlayProps {
	open?: boolean;
	/** Delay before showing the spinner (ms). If the open flag clears before this, spinner is never shown. */
	showDelayMs?: number;
	/** Minimum time spinner should remain visible once shown (ms). */
	minDurationMs?: number;
}

export default function SpinnerOverlay({
	open = false,
	showDelayMs = 200,
	minDurationMs = 300
}: SpinnerOverlayProps) {
	const [visible, setVisible] = useState(false);
	const shownAtRef = useRef<number | null>(null);
	const showTimer = useRef<number | null>(null);
	const hideTimer = useRef<number | null>(null);

	useEffect(() => {
		if (showTimer.current) {
			window.clearTimeout(showTimer.current);
			showTimer.current = null;
		}
		if (hideTimer.current) {
			window.clearTimeout(hideTimer.current);
			hideTimer.current = null;
		}

		if (open) {
			showTimer.current = window.setTimeout(() => {
				shownAtRef.current = Date.now();
				setVisible(true);
				showTimer.current = null;
			}, showDelayMs);
		} else {
			if (!visible) {
				return;
			}

			const shownAt = shownAtRef.current ?? Date.now();
			const elapsed = Date.now() - shownAt;
			const remaining = Math.max(0, minDurationMs - elapsed);
			if (remaining <= 0) {
				setVisible(false);
				shownAtRef.current = null;
			} else {
				hideTimer.current = window.setTimeout(() => {
					setVisible(false);
					shownAtRef.current = null;
					hideTimer.current = null;
				}, remaining);
			}
		}

		return () => {
			if (showTimer.current) {
				window.clearTimeout(showTimer.current);
				showTimer.current = null;
			}
			if (hideTimer.current) {
				window.clearTimeout(hideTimer.current);
				hideTimer.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, showDelayMs, minDurationMs]);

	if (!visible) {
		return null;
	}

	return (
		<Box
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 1400,
				display: "flex",
				alignItems: "center",
				justifyContent: "center"
			}}
			aria-hidden={!visible}
		>
			<CircularProgress />
		</Box>
	);
}
